/**
 * Agent 管理器
 * 负责 Agent 模块的业务逻辑
 * @author Joey
 */

import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useToast } from "@/hooks/use-toast";
import { SubscribeAgentData, subscribeAgentList } from "@/services/agent-app-list";
import { AgentData, agentList } from "@/services/agent-list";
import { agentPersonalInfoTemplateGet } from "@/services/agent-personal-info-template-get";
import { agentResolve, AgentResolveData } from "@/services/agent-resolve";
import { agentUserUpdatePersonalInfo } from "@/services/agent-user-info-update-personal-info";
import { getAgentById } from "@/services/agent-user-subinfo-get";
import { orderAttachmentUpload } from "@/services/agent-v2-order-attachment-upload";
import { agentV2UsersAvatarUpload } from "@/services/agent-v2-users-avatar-upload";
import { getArticleHeaderDrainageByApp, GetArticleHeaderDrainageByAppData } from "@/services/get-article-header-drainage-by-app";
import { updateArticleHeaderDrainageByApp } from "@/services/update-article-header-drainage-by-app";
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { canUseWindow } from "@/utils/canuseWindow";
import { isAgentAvailableForUser } from "@/utils/agent-availability";
import { CustomDomainDisabledCode, getCustomDomainDisabledCode } from "@/utils/agent-entry-route";
import { ENV } from "@/constant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { cosUploadFile } from "@/services/cos-upload-file";

// 缓存数据结构
interface CacheData {
  agents: AgentData[];
  page: number;
  size: number;
  total: number;
}

type AgentsChangeEventDetail = {
  agents: AgentData[];
};
type AgentsChangeEvent = CustomEvent<AgentsChangeEventDetail>;

type SubscribeAgentsChangeEventDetail = { subscribeAgents: SubscribeAgentData[] };
type SubscribeAgentsChangeEvent = CustomEvent<SubscribeAgentsChangeEventDetail>;

type AgentArticleSettingsChangeEventDetail = {
  agentArticleSettings: GetArticleHeaderDrainageByAppData;
};
type AgentArticleSettingsChangeEvent = CustomEvent<AgentArticleSettingsChangeEventDetail>;

type AgentIdChangeEventDetail = { agentId: string };
type AgentIdChangeEvent = CustomEvent<AgentIdChangeEventDetail>;

type CurrentAgentChangeEventDetail = { currentAgent: AgentData };
type CurrentAgentChangeEvent = CustomEvent<CurrentAgentChangeEventDetail>;

type ResolvedAgentChangeEventDetail = { resolvedAgent: AgentResolveData };
type ResolvedAgentChangeEvent = CustomEvent<ResolvedAgentChangeEventDetail>;

export class AgentManager {
  private static instance: AgentManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_LIST";
  private readonly AGENT_CREATION_ID_STORAGE_KEY = "MIUTA_AGENT_ID";
  private agentId: string = '';
  private initPromise: Promise<void> | null = null;
  private fetchAgentsPromise: Promise<Awaited<ReturnType<typeof agentList>>> | null = null;
  private resolveAgentIdPromise: Promise<AgentResolveData | undefined> | null = null;
  private hasResolvedAgentId = false;
  // 已订阅+有运营平台权限的智能体列表
  private agents: AgentData[] = [];
  // 当前智能体
  private currentAgent: AgentData = {} as AgentData;
  // 已订阅的智能体列表
  private subscribeAgents: SubscribeAgentData[] = []
  // 根据域名，解析后的智能体数据
  private resolvedAgent: AgentResolveData = {} as AgentResolveData;
  private page: number = 1;
  private size: number = 1000;
  private total: number = 0;
  // 智能体用户简介模板
  private agentPersonalInfoTemplate: string = '';
  // 智能体文章设置（页眉,底图）
  private agentArticleSettings: GetArticleHeaderDrainageByAppData = {
    articleHeaderCopy: '',
    articleDrainageImage: '',
  };

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentManager {
    if (!AgentManager.instance) {
      AgentManager.instance = new AgentManager();
    }
    return AgentManager.instance;
  }

  // 初始化
  public async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        const cached = localStorage.getItem(this.STORAGE_KEY);
        if (cached) {
          const data: CacheData = JSON.parse(cached);
          if (data.agents && data.agents.length) {
            this.setAgents(data.agents);
            this.page = data.page;
            this.size = data.size;
            this.total = data.total;
            // 后台同步数据（列表与域名解析互不依赖，并行缩短首屏）
            await Promise.all([this.fetchAgents(), this.resolveAgentId()]);
            return;
          }
        }
      } catch (error) {
        console.error("Failed to init agent list cache:", error);
        this.setAgents([]);
        this.page = 1;
        this.size = 1000;
        this.total = 0;
        throw error;
      }
      // 无缓存：列表与域名解析并行
      await Promise.all([this.fetchAgents(), this.resolveAgentId()]);
    })();

    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  // 请求 agent 列表数据（包含已订阅、运营平台权限）
  public async fetchAgents() {
    if (this.fetchAgentsPromise) {
      return this.fetchAgentsPromise;
    }

    this.fetchAgentsPromise = (async () => {
      try {
        const data = await agentList({ page: this.page, pageSize: this.size });
        this.setAgents(data.agents);
        this.page = data.currentPage;
        this.size = data.pageSize;
        this.total = data.totalPages;
        return data;
      } catch (error) {
        console.error("Failed to fetch agent list:", error);
        this.dispatchAgentsChange();
        throw error;
      }
    })();

    try {
      return await this.fetchAgentsPromise;
    } finally {
      this.fetchAgentsPromise = null;
    }
  }

  // 获取已订阅智能体列表（不包含有运营平台权限）
  public async fetchSubscribeAgents() {
    try {
      const data = await subscribeAgentList({
        page: 1,
        pageSize: 1000
      });
      this.setSubscribeAgents(data.agents);
      return data;
    } catch (error) {
      console.error("Failed to fetch subscribe agent list:", error);
      this.dispatchAgentsChange();
      throw error;
    }
  }

  // 根据域名，获取智能体id
  public async resolveAgentId(subDomain?: string) {
    const targetSubDomain = subDomain || location.origin;

    // 仅在当前运行周期已经解析过后，才复用内存 ID
    if (!subDomain && this.hasResolvedAgentId && this.agentId) {
      return this.resolvedAgent?.id ? this.resolvedAgent : ({ id: this.agentId } as AgentResolveData);
    }

    if (this.resolveAgentIdPromise) {
      return this.resolveAgentIdPromise;
    }

    this.resolveAgentIdPromise = (async () => {
      try {
        const data = await agentResolve({
          subDomain: targetSubDomain
        });
        if (!data) return;
        // 非开发环境, 设置解析后的agentId
        if (data?.id && !ENV.isDevEnv) {
          this.hasResolvedAgentId = true;
          this.setAgentId(data?.id);
          this.setAgentIdToCache(data?.id);
          // 设置解析后的智能体数据
          this.setResolvedAgent(data);
        }
        return data;
      } catch (error) {
        console.error("Failed to resolve agent id:", error);
        throw error;
      }
    })();

    try {
      return await this.resolveAgentIdPromise;
    } finally {
      this.resolveAgentIdPromise = null;
    }
  }

  /** 异步获取当前智能体的订阅用户信息并合并到 Agent */
  public async fetchAgentSubinfo(agentId: string) {
    try {
      if (this.agents.findIndex(a => a.id === agentId) < 0) return;
      const sub = await getAgentById(agentId);
      // await 期间列表可能被 setAgents/fetchAgents 替换，必须用 agentId 重新定位，避免稀疏数组污染缓存
      const idx = this.agents.findIndex(a => a.id === agentId);
      if (idx < 0) return;
      const prev = this.agents[idx];
      this.agents[idx] = {
        ...prev,
        subName: sub.userName,
        subIntro: sub.userIntro,
        subAvatar: sub.userAvatarUrl,
        subIntroduction: sub.userIntroduction,
        subscribeInfo: sub.subscribeInfo,
        applyInputTypeInfo: sub.applyInputTypeInfo,
        pushSettings: sub.pushSettings,
      } as AgentData;
      this.saveToLocal({ agents: this.agents, page: this.page, size: this.size, total: this.total });
      this.dispatchAgentsChange();
    } catch (error) {
      console.error("Failed to fetch agent subinfo:", error);
      throw error;
    }
  }

  // 更新智能体用户信息
  public async saveAgentUserInfo(agentId: string, userInfo: {
    subName: string;
    subIntro: string;
    subAvatar: string;
    subIntroduction: string;
  }) {
    const idx = this.agents.findIndex(a => a.id === agentId);
    if (idx < 0) return;
    const originAgent = this.agents[idx];
    try {
      // 乐观更新
      this.agents[idx] = {
        ...this.agents[idx],
        ...userInfo,
      } as AgentData;

      this.saveToLocal({ agents: this.agents, page: this.page, size: this.size, total: this.total });
      this.dispatchAgentsChange();

      await agentUserUpdatePersonalInfo({
        agentId,
        subUserName: userInfo.subName,
        personalTitle: userInfo.subIntro,
        introduction: userInfo.subIntroduction,
        subAvatar: userInfo.subAvatar,
      })

    } catch (error) {
      // 回滚：await 后列表可能已变，按 agentId 重新定位
      const rollbackIdx = this.agents.findIndex(a => a.id === agentId);
      if (rollbackIdx >= 0) {
        this.agents[rollbackIdx] = originAgent;
      }
      console.error("Failed to save agent user info:", error);
      throw error;
    }
  }

  // 上传用户头像
  public async uploadUserAvatar(file: File) {
    try {
      // return await agentV2UsersAvatarUpload({ file });
      const res = await cosUploadFile(file, "common");
      return res.url;
    } catch (error) {
      console.error("Failed to upload user avatar:", error);
      throw error;
    }
  }

  // 获取智能体简介模板
  public async fetchAgentIntroductionTemplate(agentId: string) {
    try {
      const res = await agentPersonalInfoTemplateGet(agentId)
      this.setAgentPersonalInfoTemplate(res.personalInformationTemplate)
    } catch (error) {
      console.error("Failed to fetch agent introduction template:", error);
      throw error;
    }
  }

  // 获取智能体文章设置（页眉,底图）
  public async fetchAgentArticleSettings(agentId: string) {
    try {
      const res = await getArticleHeaderDrainageByApp(agentId);
      this.setAgentArticleSettings(res);
      return res;
    } catch (error) {
      console.error("Failed to fetch agent article settings:", error);
      throw error;
    }
  }

  // 更新智能体文章设置（页眉,底图）
  public async saveAgentArticleSettings(agentId: string,
    settings: {
      articleHeaderCopy: string, articleDrainageImage: string
    }) {
    try {
      await updateArticleHeaderDrainageByApp({
        agentId,
        articleHeaderCopy: settings.articleHeaderCopy,
        articleDrainageImage: settings.articleDrainageImage,
      });
      this.setAgentArticleSettings(settings);
    } catch (error) {
      console.error("Failed to save agent article settings:", error);
      throw error;
    }
  }

  // 上传引流底图
  public async uploadArticleSettingImage(file: File) {
    try {
      // 获取附件链接
      const res = await cosUploadFile(file, "common");
      return res.url;
    } catch (error) {
      console.error("Failed to upload article setting image:", error);
      throw error;
    }
  }

  /** 检查是否有智能体可进入 */
  public isAgentData() {
    return this.getAgents().length > 0
  }

  // ==================== 读取写入操作 ====================
  public getAgents(): AgentData[] {
    return this.agents;
  }
  public setAgents(agents: AgentData[]) {
    // JSON 反序列化稀疏数组会得到显式 null；接口异常也可能混入空位，统一剔除避免污染后续写入
    this.agents = agents.filter((a): a is AgentData => a != null);
    if (this.currentAgent?.id) {
      this.currentAgent = this.agents.find((item) => item.id === this.currentAgent.id) ?? null;
    }
    this.saveToLocal({ agents: this.agents, page: this.page, size: this.size, total: this.total });
    this.dispatchAgentsChange();
  }

  // 获取当前智能体 id 缓存key（按 域名 隔离）
  private getCreationAgentStorageKey(path?: string) {
    if (!canUseWindow()) return this.AGENT_CREATION_ID_STORAGE_KEY;
    return `${this.AGENT_CREATION_ID_STORAGE_KEY}:${path || window.location.origin}`;
  }
  // 存入缓存id
  public setAgentIdToCache(agentId: string, path?: string) {
    if (!agentId || !canUseLocalStorage()) return;
    localStorage.setItem(this.getCreationAgentStorageKey(path), agentId);
  }
  // 取出缓存id
  public getAgentIdFromCache() {
    if (!canUseLocalStorage()) return "";
    return this.agentId || localStorage.getItem(this.getCreationAgentStorageKey()) || "";
  }

  public getSubscribeAgents() {
    return this.subscribeAgents;
  }
  public setSubscribeAgents(subscribeAgents: SubscribeAgentData[]) {
    this.subscribeAgents = subscribeAgents;
    this.dispatchSubscribeAgentsChange();
  }

  // 智能体用户简介模板
  public getAgentPersonalInfoTemplate(): string {
    return this.agentPersonalInfoTemplate;
  }
  private setAgentPersonalInfoTemplate(template: string) {
    this.agentPersonalInfoTemplate = template;
  }

  // 智能体文章设置（页眉,底图）
  public getAgentArticleSettings() {
    return this.agentArticleSettings;
  }
  public setAgentArticleSettings(settings: GetArticleHeaderDrainageByAppData) {
    this.agentArticleSettings = settings;
    this.dispatchAgentArticleSettingsChange();
  }

  // 当前智能体
  public getCurrentAgent() {
    return this.currentAgent;
  }
  public setCurrentAgent(agent: AgentData) {
    this.currentAgent = agent;
    this.dispatchCurrentAgentChange();
  }

  // 智能体ID
  public getAgentId() {
    return this.agentId || this.getCurrentAgent()?.id || this.getAgentIdFromCache();
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
    this.dispatchAgentIdChange();
  }

  // 解析后的智能体数据
  public getResolvedAgent() {
    return this.resolvedAgent;
  }
  public setResolvedAgent(agent: AgentResolveData) {
    this.resolvedAgent = agent;
    this.dispatchResolvedAgentChange();
  }

  /**
   * 用 SSR 注入或外部已知的智能体解析结果直接 seed 当前 manager。
   */
  public seedResolvedAgent(agent: AgentResolveData) {
    if (!agent?.id) return;
    this.hasResolvedAgentId = true;
    this.setAgentId(agent.id);
    this.setAgentIdToCache(agent.id);
    this.setResolvedAgent(agent);
  }

  // ==================== 缓存操作 ====================
  /** 缓存数据到本地 */
  private saveToLocal({ agents, page, size, total }: CacheData): void {
    if (canUseLocalStorage()) {
      try {
        const json = JSON.stringify({ agents, page, size, total, timestamp: Date.now() });
        localStorage.setItem(this.STORAGE_KEY, json);
      } catch (error) {
        console.error("Failed to save agent list cache:", error);
      }
    }
  }
  /** 清除所有缓存 */
  public clear(): void {
    this.setAgentId(null);
    this.setCurrentAgent({} as AgentData);
    this.setResolvedAgent({} as AgentResolveData);
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.getCreationAgentStorageKey())
  }

  // ==================== 订阅通知操作 ====================
  // 监听智能体数据变化
  public dispatchAgentsChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentsChangeEventDetail>(
        `${this.STORAGE_KEY}:change`,
        {
          detail: {
            agents: this.agents,
          },
        }
      );
      window.dispatchEvent(event);
    }
  }
  public onAgentsChange(listener: (e: AgentsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }

  // 监听已订阅智能体数据变化
  public dispatchSubscribeAgentsChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<SubscribeAgentsChangeEventDetail>(
        `${this.STORAGE_KEY}:subscribe-change`,
        {
          detail: {
            subscribeAgents: this.subscribeAgents,
          },
        }
      );
      window.dispatchEvent(event);
    }
  }
  public onSubscribeAgentsChange(listener: (e: SubscribeAgentsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:subscribe-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }

  // 监听智能体文章设置变化
  public onAgentArticleSettingsChange(listener: (e: AgentArticleSettingsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:article-settings-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentArticleSettingsChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentArticleSettingsChangeEventDetail>(
        `${this.STORAGE_KEY}:article-settings-change`,
        {
          detail: {
            agentArticleSettings: this.agentArticleSettings,
          },
        }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听当前智能体信息变化
  public onCurrentAgentChange(listener: (e: CurrentAgentChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:user-data-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);;
    }
    return () => { };
  }
  public dispatchCurrentAgentChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<CurrentAgentChangeEventDetail>(
        `${this.STORAGE_KEY}:user-data-change`,
        { detail: { currentAgent: this.currentAgent }, }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听智能体ID变化
  public onAgentIdChange(listener: (e: AgentIdChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:agent-id-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchAgentIdChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentIdChangeEventDetail>(
        `${this.STORAGE_KEY}:agent-id-change`,
        { detail: { agentId: this.agentId }, }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听解析后的智能体数据变化
  public onResolvedAgentChange(listener: (e: ResolvedAgentChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:resolved-agent-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchResolvedAgentChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<ResolvedAgentChangeEventDetail>(
        `${this.STORAGE_KEY}:resolved-agent-change`,
        { detail: { resolvedAgent: this.resolvedAgent }, }
      );
      window.dispatchEvent(event);
    }
  }
}

// ============= Hooks =============
const useAgentData = (options: { isInitial: boolean } = {
  isInitial: true
}) => {
  const { isInitial } = options;
  const { t } = useTranslation();
  const [agents, setAgents] = useState(agentManager.getAgents());
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();
  const isCustomDomain = ENV.isProdSubEnv || ENV.isTestSubEnv;

  const init = async () => {
    setLoading(true);
    try {
      await agentManager.init();
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  // 前往智能体页面
  const navigateToFirstAgent = () => {
    if (agents?.find(item => item.id === agentManager.getAgentId())) {
      navigate(`/agent/creation/${agentManager.getAgentId()}/`);
    } else {
      navigate('/')
    }
  }

  useEffect(() => {
    if (!isInitial) return;
    init();
  }, [isInitial]);

  useEffect(() => {
    return agentManager.onAgentsChange((e) => {
      setAgents(e.detail.agents);
    });
  }, []);

  // 手动订阅，触发更新
  // const [domainEntrySync, setDomainEntrySync] = useState(0);
  // useEffect(() => {
  //   const bump = () => setDomainEntrySync((n) => n + 1);
  //   const offId = agentManager.onAgentIdChange(bump);
  //   const offResolved = agentManager.onResolvedAgentChange(bump);
  //   return () => {
  //     offId();
  //     offResolved();
  //   };
  // }, []);

  // 列表是否有可用智能体
  const hasAvailableAgent = useMemo(
    () => agents.some((item) => isAgentAvailableForUser(item)),
    [agents],
  );


  /** 定制域名且存在任意可用智能体时，选系统页上「停用/账号异常」说明（与入口解析一致） */
  const customDomainDisabledDescription = useMemo(() => {
    if (!isCustomDomain) return undefined;
    const code = getCustomDomainDisabledCode({
      agents,
      domainAgentId: agentManager.getAgentId(),
      domainAgentStatus: agentManager.getResolvedAgent()?.status,
    });
    if (code === undefined) return undefined;
    return code === CustomDomainDisabledCode.ACCOUNT_DISABLED
      ? t("select_system.custom_domain_disabled.account")
      : t("select_system.custom_domain_disabled.agent");
  }, [agents, isCustomDomain, t]);

  return {
    agents,
    loading,
    navigateToFirstAgent,
    isAgentData: agentManager.isAgentData(),
    hasAvailableAgent,
    isCustomDomain,
    customDomainDisabledDescription,
  }
}

const useGetAgentList = () => {
  const agents = agentManager.getAgents();

  return {
    agents,
    isAgentData: agentManager.isAgentData(),
  }
}

// 获取当前的 Agent，并且初始化获取 Agent 详情和文章设置
const useCurrentAgent = (props: {
  isFetchTemplate?: boolean;
} = {}) => {
  const { isFetchTemplate } = props;
  const params = useParams();
  const navigate = useI18nNavigate();
  const isAgentCreationRoute = useMatch("/agent/creation/:id/*");
  // 创建页用路由 id，否则用 manager 中的当前智能体 id
  const resolveAgentId = () =>
    ((isAgentCreationRoute ? params.id : "") || agentManager.getAgentId() || "") as string;
  const agentId = resolveAgentId();

  // 已成功拉取过 subinfo 的 agentId，避免列表多次 dispatch 重复请求
  const lastSubinfoFetchedIdRef = useRef<string | null>(null);
  // 与 last 不同：仅防止并发重复，成功后会置 last
  const subinfoInFlightIdRef = useRef<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState<AgentData>(() => {
    const list = agentManager.getAgents();
    const found = list.find((a) => a.id === agentId);
    if (!found && list.length) {
      navigate('/agent/');
    }
    return found ?? ({} as AgentData);
  });

  useEffect(() => {
    const unsubscribe = agentManager.onAgentsChange((e) => {
      const id = resolveAgentId();
      const found = e.detail.agents.find((a) => a.id === id);
      if (found) setAgent((value) => ({ ...value, ...found }));
    });
    return unsubscribe;
  }, [params.id, isAgentCreationRoute]);

  useEffect(() => {
    lastSubinfoFetchedIdRef.current = null;
    subinfoInFlightIdRef.current = null;
  }, [agentId]);

  const tryFetchAgentSubInfo = useCallback(async () => {
    const list = agentManager.getAgents();
    const id = resolveAgentId();
    const found = list.find((a) => a.id === id);
    if (!found) return;

    if (lastSubinfoFetchedIdRef.current === id) return;
    if (subinfoInFlightIdRef.current === id) return;

    const fetchedId = found.id;
    subinfoInFlightIdRef.current = fetchedId;
    setLoading(true);
    try {
      await agentManager.fetchAgentSubinfo(fetchedId);
      // 导航切换 agent 后，丢弃过期请求的结果
      if (resolveAgentId() === fetchedId) {
        lastSubinfoFetchedIdRef.current = fetchedId;
      }
    } catch (error) {
      console.error("Failed to fetch agent subinfo:", error);
    } finally {
      if (subinfoInFlightIdRef.current === fetchedId) {
        subinfoInFlightIdRef.current = null;
      }
      if (resolveAgentId() === fetchedId) {
        setLoading(false);
      }
    }
  }, [agentId]);

  // 获取智能体简介模板
  const fetchAgentIntroductionTemplate = async () => {
    const list = agentManager.getAgents();
    const id = resolveAgentId();
    const found = list.find((a) => a.id === id);
    if (found) {
      setLoading(true);
      try {
        await agentManager.fetchAgentIntroductionTemplate(found.id);
      } catch (error) {
        console.error("Failed to fetch agent introduction template:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    tryFetchAgentSubInfo();
    if (isFetchTemplate) {
      fetchAgentIntroductionTemplate();
    }
  }, [agentId, isFetchTemplate, tryFetchAgentSubInfo]);

  // 列表异步到达时补拉 subinfo（与 agentId/isFetchTemplate 变更解耦）
  useEffect(() => {
    const unsubscribe = agentManager.onAgentsChange(() => {
      tryFetchAgentSubInfo();
    });
    return unsubscribe;
  }, [tryFetchAgentSubInfo]);

  return {
    agent,
    loading,
    introductionTemplate: agentManager.getAgentPersonalInfoTemplate() || '',
  }
}

// 单纯获取当前的 Agent 信息
const useGetCurrentAgent = () => {
  const params = useParams();
  const isAgentCreationRoute = useMatch('/agent/creation/:id/*');
  const navigate = useI18nNavigate();

  const [currentAgent, setCurrentAgent] = useState<AgentData>(agentManager.getCurrentAgent());
  const [resolvedAgent, setResolvedAgent] = useState<AgentResolveData>(agentManager.getResolvedAgent());

  useEffect(() => {
    let mounted = true;

    const syncCurrentAgentByResolvedId = async () => {
      try {
        // 仅在创建页使用路由参数，避免其他路由的 params.id 干扰
        let agentId = (isAgentCreationRoute ? params.id : "") || agentManager.getAgentId();
        if (!mounted) return;

        // 线上环境根据子域名请求的智能体ID为准
        if (ENV.isProdSubEnv || ENV.isTestSubEnv) {
          // 以 resolveAgentId 返回值作为指定 ID
          const resolvedData = await agentManager.resolveAgentId();
          const resolvedId = resolvedData?.id;
          if (!resolvedId) return;
          agentId = resolvedId;
        }

        if (!agentId) return;

        // 将最终 agentId 写回 manager，保证其他模块在任意路由都能读到一致的当前智能体
        agentManager.setAgentId(agentId);
        agentManager.setAgentIdToCache(agentId);

        const nextAgent = agentManager.getAgents().find((item) => item.id === agentId) ?? null;
        setCurrentAgent(nextAgent);
        agentManager.setCurrentAgent(nextAgent);

        // 线上子域名 和 测试子域名
        // 当路由的ID, 与解析出来的 ID 不一致时，强制替换到正确路由，避免历史堆栈污染
        if (ENV.isProdSubEnv || ENV.isTestSubEnv) {
          if (isAgentCreationRoute && params.id !== agentId) {
            await navigate(`/agent/creation/${agentId}/`, { replace: true });
            location.reload()
          }
        }
      } catch (error) {
        console.error("Failed to sync current agent by resolved id:", error);
      }
    };

    syncCurrentAgentByResolvedId();

    // agent 列表异步到达后再次同步，避免首次找不到 agent 而导致 currentAgent 为空
    const unsubscribeAgents = agentManager.onAgentsChange(() => {
      syncCurrentAgentByResolvedId();
    });

    return () => {
      mounted = false;
      unsubscribeAgents?.();
    };
  }, [params.id, isAgentCreationRoute]);

  useEffect(() => {
    return agentManager.onCurrentAgentChange((e) => {
      setCurrentAgent(e.detail.currentAgent);
    });
  }, []);

  useEffect(() => {
    return agentManager.onResolvedAgentChange((e) => {
      setResolvedAgent(e.detail.resolvedAgent);
    });
  }, []);

  // 获取智能体过期时间
  const agentExpiresAt = resolvedAgent?.agentExpiresAt ?? currentAgent?.agentExpiresAt;

  const resolvedAgentData = Object.keys(resolvedAgent).length > 0 ? resolvedAgent : currentAgent

  return {
    currentAgent: currentAgent || {} as AgentData,
    // 如果解析后的智能体数据为空，则使用当前智能体数据
    resolvedAgent: resolvedAgentData || {} as AgentResolveData,
    isAgentExpired: agentExpiresAt && (agentExpiresAt * 1000) < Date.now(),
  };
}

// 智能体用户信息保存
const useAgentUserinfoSave = () => {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const agentId = agentManager.getAgentId();
  const save = async (userInfo: {
    subName: string;
    subIntro: string;
    subAvatar: string;
    subIntroduction: string;
  }) => {
    if (!agentId) {
      console.error("Agent id is required");
      return;
    }
    setLoading(true);
    try {
      await agentManager.saveAgentUserInfo(agentId, userInfo);
    } catch (error) {
      console.error("Failed to save agent userinfo:", error);
    } finally {
      setLoading(false);
    }
  }

  return {
    save,
    loading,
  }
}

// 智能体用户头像上传
const useUploadUserAvatar = () => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const upload = async (file: File) => {
    setLoading(true);
    try {
      return await agentManager.uploadUserAvatar(file);
    } catch (error) {
      console.error("Failed to upload user avatar:", error);
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    upload,
    loading,
  }
}

// 获取/保存智能体文章设置（页眉,底图）
const useAgentArticleSettings = () => {
  const params = useParams();
  const toast = useToast();
  const [saveLoading, setSaveLoading] = useState(false);
  const [articleSettings, setArticleSettings] = useState<GetArticleHeaderDrainageByAppData>(agentManager.getAgentArticleSettings());


  // 获取智能体文章设置（页眉,底图）
  const fetchAgentArticleSettings = async () => {
    try {
      await agentManager.fetchAgentArticleSettings(params.id);
    } catch (error) {
      console.error("Failed to fetch agent article settings:", error);
      toast.showToastByCode(error);
    }
  }

  // 保存智能体文章设置（页眉,底图）
  const saveAgentArticleSettings = async (settings: GetArticleHeaderDrainageByAppData) => {
    setSaveLoading(true);
    try {
      await agentManager.saveAgentArticleSettings(params.id, settings);
    } catch (error) {
      console.error("Failed to save agent article settings:", error);
      toast.showToastByCode(error);
    } finally {
      setSaveLoading(false);
    }
  }

  // 上传引流底图
  const uploadArticleSettingImage = async (file: File) => {
    try {
      return await agentManager.uploadArticleSettingImage(file);
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to upload article setting image:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchAgentArticleSettings();

    return () => {
      agentManager.setAgentArticleSettings({
        articleHeaderCopy: '',
        articleDrainageImage: '',
      })
    }
  }, [params.id]);

  useEffect(() => {
    return agentManager.onAgentArticleSettingsChange((e) => {
      setArticleSettings(e.detail.agentArticleSettings);
    });
  }, []);

  return {
    articleSettings,
    saveLoading,
    saveAgentArticleSettings,
    uploadArticleSettingImage,
  }
}

// 已订阅的智能体数据
const useSubscribeAgentData = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [subscribeAgents, setSubscribeAgents] = useState(agentManager.getSubscribeAgents());

  const init = async () => {
    setLoading(true);
    try {
      await agentManager.fetchSubscribeAgents();
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    return agentManager.onSubscribeAgentsChange((e) => {
      setSubscribeAgents(e.detail.subscribeAgents);
    });
  }, []);

  // 检查 agent 是否有字段包含 '3'
  const hasCreation = (agent: SubscribeAgentData) => {
    const { applyInputTypeInfo } = agent;
    const fields = [
      applyInputTypeInfo.videoApplyInputType,
      applyInputTypeInfo.blogApplyInputType,
      applyInputTypeInfo.momentsApplyInputType,
      applyInputTypeInfo.graphicApplyInputType,
      applyInputTypeInfo.articleApplyInputType,
    ];
    return fields.some(field => {
      if (!field) return false;
      const values = field.split(',').map(v => parseInt(v.trim(), 10));
      return values.includes(3);
    });
  };

  // 检查当前智能体是否包含3
  const showCreation = useMemo(() => {
    return subscribeAgents.filter(item => item.id === agentManager.getAgentId()).some(hasCreation);
  }, [subscribeAgents]);

  // 过滤出至少有一个字段包含 '3' 的 agent
  const filteredSubscribeAgents = useMemo(() => {
    return subscribeAgents.filter(hasCreation);
  }, [subscribeAgents]);

  return {
    subscribeAgents,
    loading,
    showCreation,
    filteredSubscribeAgents
  }
}

// 解析域名的智能体id
const useResolveAgentId = (props: {
  isSetIdToCache?: boolean
} = {}) => {
  const { isSetIdToCache } = props;
  const [agentId, setAgentId] = useState('')
  const [resolvedAgent, setResolvedAgent] = useState<AgentResolveData | null>(null);
  const { showToastByCode } = useToast();

  const init = async () => {
    try {
      const data = await agentManager.resolveAgentId()
      const id = data?.id;
      setResolvedAgent(data || null);
      if (id) {
        setAgentId(id)
        isSetIdToCache && agentManager.setAgentIdToCache(id)
      }
    } catch (err) {
      console.error(err)
      showToastByCode(err)
    }
  }

  useEffect(() => {
    init()
  }, [])

  return {
    agentId,
    resolvedAgent,
  }
}

export const agentsHooks = {
  useAgentData,
  useCurrentAgent,
  useGetAgentList,
  useGetCurrentAgent,
  useAgentUserinfoSave,
  useUploadUserAvatar,
  useAgentArticleSettings,
  useSubscribeAgentData,
  useResolveAgentId,
}

// 导出单例实例
export const agentManager = AgentManager.getInstance();
